import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Volume2, VolumeX } from 'lucide-react';
import { useAskAiVoice } from '../hooks/useAskAiVoice';
import { unlockBrowserSpeech } from '../utils/browserSpeech';
import AaCoreAiName from './AaCoreAiName';
import AskAiRichText from './AskAiRichText';
import AskAiBookingReviewCard from './AskAiBookingReviewCard';
import { DateChoicePicker, TimeSlotGrid, looksLikeTimeChip } from './AskAiSchedulePickers';
import { AskAiMobileFullscreenButton, AskAiMobileScrim, useAskAiMobileFullscreen } from './AskAiMobileFullscreen';
import { apiService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import { getBrowserTimeZone } from '../utils/timezoneUtils';
import { askAiRequestErrorMessage } from '../utils/askAiErrors';
import { getOrCreateAskAiSessionId, resetAskAiSessionId } from '../utils/askAiSession';
import { isBusinessOwnerForBusiness } from '../utils/roleUtils';
import {
  AskAiBookingDraft,
  AskAiBookingService,
  AskAiBookingStatus,
  AskAiReply,
  AskAiTurn,
} from '../types';

export const OWNER_APPOINTMENT_BOOKED_EVENT = 'aacore:owner-appointment-booked';

const ACCENT = '#0B4778';
const INK = '#ffffff';
const TEXT = '#111827';

function storageKeyFor(businessId: string): string {
  return `aac-owner-ask:${businessId}`;
}

function draftFromStatus(booking?: AskAiBookingStatus): AskAiBookingDraft {
  if (!booking?.wanted) return {};
  return {
    wanted: true,
    serviceNames: (booking.services || []).map((service) => service.name),
    serviceIds: (booking.services || []).map((service) => service.id).filter(Boolean),
    date: booking.date || '',
    time: booking.time || '',
    professionalId: booking.professionalId || '',
    professionalName: booking.professionalName || '',
    anyProfessional: Boolean(booking.anyProfessional),
    sameProfessional: typeof booking.sameProfessional === 'boolean' ? booking.sameProfessional : undefined,
    serviceProfessionals: booking.serviceProfessionals || [],
    recurring: Boolean(booking.recurring),
    intervalWeeks: booking.intervalWeeks,
    occurrenceCount: booking.occurrenceCount,
    serviceAddress: booking.serviceAddress,
    rememberServiceAddress: booking.rememberServiceAddress,
    needsAddress: Boolean(booking.needsAddress),
    customerId: booking.customerId || '',
    customerName: booking.customerName || '',
    customerFirstName: booking.customerFirstName || '',
    customerLastName: booking.customerLastName || '',
    customerEmail: booking.customerEmail || '',
    customerPhone: booking.customerPhone || '',
    newCustomer: Boolean(booking.newCustomer),
    emailMatchConfirmed: Boolean(booking.emailMatchConfirmed),
    customerChoices: booking.customerChoices || [],
    privateNotes: booking.privateNotes || '',
  };
}

function chipOverride(chip: string, booking?: AskAiBookingStatus): AskAiBookingDraft | undefined {
  if (chip === 'New walk-in customer') {
    return { wanted: true, newCustomer: true, customerId: '', customerChoices: [], emailMatchConfirmed: false };
  }
  if (chip === 'Book for this customer') {
    const choice = booking?.customerChoices?.[0];
    return {
      wanted: true,
      customerId: choice?.id || booking?.customerId || '',
      customerName: booking?.customerName || choice?.label || '',
      newCustomer: false,
      emailMatchConfirmed: true,
    };
  }
  if (chip === 'Use a different customer') {
    return {
      wanted: true,
      customerId: '',
      customerName: '',
      customerQuery: '',
      customerFirstName: '',
      customerLastName: '',
      customerEmail: '',
      customerPhone: '',
      newCustomer: false,
      emailMatchConfirmed: false,
      customerChoices: [],
    };
  }
  if (chip === 'Same professional') return { wanted: true, sameProfessional: true, serviceProfessionals: [] };
  if (chip === 'Different professionals') return { wanted: true, sameProfessional: false };
  if (chip === 'Use saved address') return { wanted: true, useSavedAddress: true };
  if (chip === 'One-time visit') return { wanted: true, recurring: false };
  const weeks = chip.match(/^Every (\d+) weeks?$/i);
  if (weeks) return { wanted: true, recurring: true, intervalWeeks: Number(weeks[1]) };
  const visits = chip.match(/^(\d+) visits?$/i);
  if (visits) return { wanted: true, recurring: true, occurrenceCount: Number(visits[1]) };
  const choice = (booking?.customerChoices || []).find((item) => item.label === chip);
  if (choice) {
    return {
      wanted: true,
      customerId: choice.id,
      customerName: choice.label,
      customerQuery: '',
      customerPhone: '',
      customerFirstName: '',
      customerLastName: '',
      newCustomer: false,
      emailMatchConfirmed: true,
      customerChoices: [],
    };
  }
  return undefined;
}

function ServiceChoices({
  groups,
  preselectedIds,
  disabled,
  onContinue,
}: {
  groups: NonNullable<AskAiBookingStatus['serviceGroups']>;
  preselectedIds: string[];
  disabled?: boolean;
  onContinue: (services: AskAiBookingService[]) => void;
}) {
  const [selected, setSelected] = useState<string[]>(preselectedIds);
  const toggle = (id: string) => {
    setSelected((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  };
  const chosen = groups.flatMap((group) => group.services).filter((service) => selected.includes(service.id));
  return (
    <div className="mt-2 space-y-2">
      {groups.map((group) => (
        <div key={group.category || 'services'}>
          {group.category ? <p className="m-0 mb-1 text-xs font-semibold opacity-70">{group.category}</p> : null}
          <div className="flex flex-col gap-1">
            {group.services.map((service) => {
              const on = selected.includes(service.id);
              return (
                <button
                  key={service.id}
                  type="button"
                  disabled={disabled}
                  className="text-left text-xs border px-2 py-1.5 cursor-pointer disabled:opacity-60"
                  style={{
                    borderColor: on ? ACCENT : `${TEXT}22`,
                    background: on ? `${ACCENT}14` : 'transparent',
                    borderRadius: 8,
                    color: TEXT,
                  }}
                  onClick={() => toggle(service.id)}
                >
                  {service.name}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <button
        type="button"
        disabled={disabled || chosen.length === 0}
        className="text-xs font-semibold px-2 py-1.5 cursor-pointer disabled:opacity-60"
        style={{ background: ACCENT, color: INK, borderRadius: 8, border: 0 }}
        onClick={() => onContinue(chosen)}
      >
        Continue
      </button>
    </div>
  );
}

const BusinessOwnerAskAiWidget: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { currentBusiness } = useBusiness();
  const businessId = currentBusiness?.id || '';
  const isOwner = Boolean(businessId && isBusinessOwnerForBusiness(user, businessId));
  const onDashboard = location.pathname.startsWith('/dashboard/business');
  const storageKey = storageKeyFor(businessId || 'none');
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [bookingBusy, setBookingBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [statusError, setStatusError] = useState(false);
  const [history, setHistory] = useState<AskAiTurn[]>([]);
  const [bookingDraft, setBookingDraft] = useState<AskAiBookingDraft>({});
  const [bookingByIndex, setBookingByIndex] = useState<Record<number, AskAiBookingStatus>>({});
  const threadRef = useRef<HTMLDivElement>(null);
  const bookingsRef = useRef(bookingByIndex);
  bookingsRef.current = bookingByIndex;
  const { mobileFullscreen, mobileCompact, toggleFullscreen } = useAskAiMobileFullscreen(open);

  const persist = useCallback((nextHistory: AskAiTurn[], nextDraft: AskAiBookingDraft, nextBookings: Record<number, AskAiBookingStatus>) => {
    try {
      sessionStorage.setItem(`${storageKey}:history`, JSON.stringify(nextHistory));
      sessionStorage.setItem(`${storageKey}:draft`, JSON.stringify(nextDraft));
      sessionStorage.setItem(`${storageKey}:bookings`, JSON.stringify(nextBookings));
    } catch {
      // session storage can be unavailable
    }
  }, [storageKey]);

  useEffect(() => {
    if (!isOwner || !onDashboard) return;
    try {
      const savedHistory = JSON.parse(sessionStorage.getItem(`${storageKey}:history`) || '[]');
      const savedDraft = JSON.parse(sessionStorage.getItem(`${storageKey}:draft`) || '{}');
      const savedBookings = JSON.parse(sessionStorage.getItem(`${storageKey}:bookings`) || '{}');
      setHistory(Array.isArray(savedHistory) ? savedHistory : []);
      setBookingDraft(savedDraft && typeof savedDraft === 'object' ? savedDraft : {});
      setBookingByIndex(savedBookings && typeof savedBookings === 'object' ? savedBookings : {});
    } catch {
      setHistory([]);
      setBookingDraft({});
      setBookingByIndex({});
    }
    setOpen(sessionStorage.getItem(`${storageKey}:open`) === '1');
  }, [isOwner, onDashboard, storageKey]);

  useEffect(() => {
    if (!isOwner || !businessId) {
      setEnabled(false);
      return;
    }
    let cancelled = false;
    apiService.getOwnerAskAiStatus(businessId)
      .then((data) => {
        if (!cancelled) setEnabled(Boolean(data.enabled));
      })
      .catch(() => {
        if (!cancelled) setEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOwner, businessId]);

  useEffect(() => {
    const node = threadRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [history, pending, open]);

  const voice = useAskAiVoice({
    active: open,
    onFinalTranscript: (text) => {
      const query = text.trim();
      if (query) void ask(query);
    },
  });
  const askRef = useRef<(query: string, override?: AskAiBookingDraft) => void>(() => undefined);
  const speakRef = useRef(voice.speak);
  speakRef.current = voice.speak;

  const applyReply = (booking?: AskAiBookingStatus) => {
    const next = draftFromStatus(booking);
    setBookingDraft(next);
    return next;
  };

  const ask = async (query: string, override?: AskAiBookingDraft) => {
    if (!businessId || pending || bookingBusy) return;
    voice.stopListening();
    voice.stopSpeaking();
    unlockBrowserSpeech();
    const prior = history.map((turn) => ({ role: turn.role, content: turn.content }));
    const bookingPayload = override ? { ...bookingDraft, ...override, wanted: true } : bookingDraft;
    setPending(true);
    setStatus('');
    setStatusError(false);
    setHistory((current) => [...current, { role: 'user', content: query }]);
    try {
      const data: AskAiReply = await apiService.askOwner(businessId, {
        query,
        history: prior,
        booking: bookingPayload,
        timeZone: getBrowserTimeZone(),
        sessionId: getOrCreateAskAiSessionId(storageKey),
      });
      const answer = data.answer || 'Here is what I can share.';
      void speakRef.current(answer);
      const nextDraft = applyReply(data.booking);
      setHistory((current) => {
        const next = [...current, { role: 'assistant' as const, content: answer }];
        const bookings = {
          ...bookingsRef.current,
          ...(data.booking ? { [next.length - 1]: data.booking } : {}),
        };
        setBookingByIndex(bookings);
        persist(next, nextDraft, bookings);
        return next;
      });
    } catch (error: any) {
      setHistory((current) => current.slice(0, -1));
      setStatus(askAiRequestErrorMessage(error));
      setStatusError(true);
    } finally {
      setPending(false);
    }
  };
  askRef.current = (query, override) => {
    void ask(query, override);
  };

  const confirmBook = async (booking: AskAiBookingStatus, messageIndex: number) => {
    if (!businessId || bookingBusy || !booking.ready) return;
    if (!booking.businessId || !booking.assignments?.length || (!booking.scheduledAt && !booking.recurring)) {
      setStatus('I am missing booking details. Please tell me the customer, service, date, and time again.');
      setStatusError(true);
      return;
    }
    const walkIn = Boolean(booking.newCustomer) && !booking.customerId;
    if (!booking.customerId && !(walkIn && booking.customerFirstName)) {
      setStatus('I still need the customer for this appointment.');
      setStatusError(true);
      return;
    }
    setBookingBusy(true);
    setStatus('');
    setStatusError(false);
    const confirmTurn: AskAiTurn = { role: 'user', content: 'Confirm and Book' };
    const historyForRequest = [
      ...history.map((turn) => ({ role: turn.role, content: turn.content })),
      { role: 'user' as const, content: confirmTurn.content },
    ];
    setHistory((current) => {
      const next = [...current, confirmTurn];
      persist(next, bookingDraft, bookingsRef.current);
      return next;
    });
    const draft = draftFromStatus(booking);
    try {
      const verified = await apiService.confirmOwnerAskBooking(businessId, {
        booking: draft,
        timeZone: getBrowserTimeZone(),
        history: historyForRequest,
        sessionId: getOrCreateAskAiSessionId(storageKey),
      });
      if (!verified.booking?.ready || !verified.booking.assignments?.length) {
        const answer = verified.answer || 'That booking needs another detail before I can save it.';
        applyReply(verified.booking);
        setHistory((current) => {
          const next = [...current, { role: 'assistant' as const, content: answer }];
          const bookings = {
            ...bookingsRef.current,
            [messageIndex]: { ...booking, ready: false },
            ...(verified.booking ? { [next.length - 1]: verified.booking } : {}),
          };
          setBookingByIndex(bookings);
          persist(next, draftFromStatus(verified.booking), bookings);
          return next;
        });
        return;
      }
      let customerId = verified.booking.customerId || booking.customerId || '';
      let createdNote = '';
      if (!customerId) {
        const invited = await apiService.createCustomerInvitation({
          businessId,
          firstName: verified.booking.customerFirstName || booking.customerFirstName || '',
          lastName: verified.booking.customerLastName || booking.customerLastName || undefined,
          email: verified.booking.customerEmail || booking.customerEmail || undefined,
          phone: verified.booking.customerPhone || booking.customerPhone || undefined,
        });
        customerId = invited.userId;
        createdNote = invited.emailSent
          ? ` I created the customer and sent an invitation to ${verified.booking.customerEmail || booking.customerEmail}.`
          : ' I created the walk-in customer.';
      }
      const series = Boolean(verified.booking.recurring)
        && Number(verified.booking.intervalWeeks) >= 1
        && Number(verified.booking.occurrenceCount) >= 1;
      let appointmentId = '';
      let visitCount = 1;
      if (series) {
        const created = await apiService.createRecurringAppointments({
          businessId,
          customerId,
          mode: 'STRICT',
          intervalWeeks: Number(verified.booking.intervalWeeks),
          occurrenceCount: Number(verified.booking.occurrenceCount),
          startDate: verified.booking.date || '',
          startTime: verified.booking.time || '',
          clientTimeZone: getBrowserTimeZone(),
          assignments: verified.booking.assignments,
          serviceAddress: verified.booking.serviceAddress || booking.serviceAddress,
          rememberServiceAddress: verified.booking.rememberServiceAddress === true,
          bookedViaAi: true,
          aiChatSessionId: getOrCreateAskAiSessionId(storageKey),
        });
        appointmentId = created.appointmentIds?.[0] || '';
        visitCount = Array.isArray(created.appointmentIds) ? created.appointmentIds.length : Number(verified.booking.occurrenceCount);
      } else {
        const created = await apiService.createAppointment({
          businessId,
          customerId,
          scheduledAt: verified.booking.scheduledAt || '',
          bookingContext: 'business',
          allowOverride: true,
          assignments: verified.booking.assignments,
          serviceAddress: verified.booking.serviceAddress || booking.serviceAddress,
          rememberServiceAddress: verified.booking.rememberServiceAddress === true,
          bookedViaAi: true,
          aiChatSessionId: getOrCreateAskAiSessionId(storageKey),
        });
        appointmentId = created.id;
      }
      const notes = verified.booking.privateNotes || booking.privateNotes || '';
      if (notes.trim()) {
        try {
          await apiService.updateCustomerPrivateNotes(customerId, businessId, notes.trim());
        } catch {
          createdNote += ' I could not save the private notes.';
        }
      }
      window.dispatchEvent(new CustomEvent(OWNER_APPOINTMENT_BOOKED_EVENT, {
        detail: { businessId },
      }));
      const who = verified.booking.customerName || booking.customerName || 'the customer';
      const followUp = series
        ? `The recurring series for ${who} is booked (${visitCount} visit${visitCount === 1 ? '' : 's'}).${createdNote}`
        : `The appointment for ${who} is booked.${createdNote}`;
      const cleared = {};
      setBookingDraft(cleared);
      setHistory((current) => {
        const next = [...current, { role: 'assistant' as const, content: followUp }];
        const bookings = {
          ...bookingsRef.current,
          [messageIndex]: { ...booking, ready: false },
          [next.length - 1]: {
            ...verified.booking,
            ready: false,
            booked: true,
            appointmentId: appointmentId || undefined,
            appointmentUrl: appointmentId ? `/appointments/${appointmentId}` : '/dashboard/business?section=appointments',
          },
        };
        setBookingByIndex(bookings);
        persist(next, cleared, bookings);
        return next;
      });
    } catch (error: any) {
      setStatus(error?.response?.data?.message || error?.message || 'I could not complete that booking.');
      setStatusError(true);
    } finally {
      setBookingBusy(false);
    }
  };

  if (!onDashboard || !isOwner || !enabled) return null;

  const closePanel = () => {
    voice.stopListening();
    voice.stopSpeaking();
    sessionStorage.setItem(`${storageKey}:open`, '0');
    setOpen(false);
  };

  const resetChat = () => {
    resetAskAiSessionId(storageKey);
    setHistory([]);
    setBookingDraft({});
    setBookingByIndex({});
    setStatus('');
    persist([], {}, {});
  };

  return (
    <>
      <AskAiMobileScrim show={mobileCompact} onClose={closePanel} />
      <div
        className={`owner-ask-ai-dock fixed flex flex-col-reverse items-end pointer-events-none ${
          mobileFullscreen
            ? 'owner-ask-ai-dock-full inset-0 z-[1100] gap-0'
            : `${mobileCompact ? 'z-[1100]' : 'z-50'} gap-3 right-3 bottom-[calc(14px+env(safe-area-inset-bottom))] min-[501px]:right-6 min-[501px]:bottom-[22px]`
        }`}
        style={{ width: mobileFullscreen ? '100%' : 'min(23rem, calc(100vw - 2.3rem))' }}
      >
        <button
          type="button"
          className={`${open ? 'max-md:hidden' : ''} pointer-events-auto inline-flex items-center self-end gap-2 rounded-full px-4 py-3 shadow-lg font-semibold text-sm`}
          style={{ background: ACCENT, color: INK }}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls="owner-ask-dialog"
          onClick={() => {
            const next = !open;
            sessionStorage.setItem(`${storageKey}:open`, next ? '1' : '0');
            setOpen(next);
          }}
        >
          <AaCoreAiName ask color={INK} aiColor="#FDE68A" iconClassName="w-5 h-5" className="text-sm" />
        </button>
        {open && (
          <div className={`pointer-events-auto w-full ${mobileFullscreen ? 'h-full' : ''}`}>
            <div
              id="owner-ask-dialog"
              role="dialog"
              aria-modal={mobileCompact || mobileFullscreen}
              aria-labelledby="owner-ask-dialog-title"
              className="flex flex-col w-full overflow-hidden bg-white border shadow-2xl"
              style={{
                height: mobileFullscreen ? '100%' : 'min(78vh, 36rem)',
                color: TEXT,
                borderColor: `${TEXT}22`,
                borderRadius: mobileFullscreen ? 0 : 16,
              }}
            >
              <header className={`flex flex-col gap-1 px-4 pb-3 flex-shrink-0 ${mobileFullscreen ? 'pt-[max(1rem,env(safe-area-inset-top))]' : 'pt-4'}`}>
                <div className="flex justify-between items-start gap-3">
                  <h2 id="owner-ask-dialog-title" className="text-lg font-semibold m-0 tracking-tight min-w-0">
                    <AaCoreAiName ask color={TEXT} aiColor={ACCENT} iconClassName="w-5 h-5" className="text-lg" />
                  </h2>
                  <div className="flex items-center gap-2 shrink-0">
                    {voice.canSpeak ? (
                      <button
                        type="button"
                        className="w-8 h-8 border bg-transparent inline-flex items-center justify-center cursor-pointer"
                        style={{ borderColor: `${TEXT}22`, color: voice.speaking ? ACCENT : TEXT, borderRadius: 8 }}
                        aria-label={voice.voiceReplies ? 'Mute spoken replies' : 'Unmute spoken replies'}
                        onClick={voice.toggleVoiceReplies}
                      >
                        {voice.voiceReplies ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                      </button>
                    ) : null}
                    {history.length > 0 && (
                      <button
                        type="button"
                        className="h-8 px-3 text-xs border bg-transparent cursor-pointer"
                        style={{ borderColor: `${TEXT}22`, color: TEXT, borderRadius: 8 }}
                        onClick={resetChat}
                      >
                        New chat
                      </button>
                    )}
                    <AskAiMobileFullscreenButton fullscreen={mobileFullscreen} onToggle={toggleFullscreen} textColor={TEXT} />
                    <button
                      type="button"
                      className="w-8 h-8 border bg-transparent text-xl leading-none cursor-pointer"
                      style={{ borderColor: `${TEXT}22`, color: TEXT, borderRadius: 8 }}
                      aria-label="Close Ask AACore AI"
                      onClick={closePanel}
                    >
                      ×
                    </button>
                  </div>
                </div>
                <p className="w-full mt-0 mb-0 text-sm" style={{ color: `${TEXT}99` }}>
                  Book for an existing customer or a walk-in
                </p>
              </header>
              <div ref={threadRef} className="flex-1 min-h-0 overflow-auto flex flex-col gap-2 px-4 pb-2" aria-live="polite">
                {history.length === 0 && (
                  <div className="flex flex-wrap gap-2">
                    {['Book an appointment', 'New walk-in customer'].map((hint) => (
                      <button
                        key={hint}
                        type="button"
                        className="text-xs bg-transparent border rounded-full px-2 py-1 cursor-pointer"
                        style={{ borderColor: `${TEXT}22`, color: TEXT }}
                        onClick={() => void ask(hint, chipOverride(hint))}
                      >
                        {hint}
                      </button>
                    ))}
                  </div>
                )}
                {history.map((turn, index) => {
                  const booking = bookingByIndex[index];
                  const isUser = turn.role === 'user';
                  const stale = pending || bookingBusy || index < history.length - 1;
                  const showServices = Boolean(booking?.serviceGroups && booking.serviceGroups.length > 0 && !booking.ready && !booking.booked);
                  const showDates = Boolean(
                    booking
                    && !booking.ready
                    && !booking.booked
                    && !showServices
                    && !(booking.technicianGroups && booking.technicianGroups.length > 0)
                    && (booking.dateOptions?.length || booking.showCalendar)
                    && !(booking.missing || []).includes('serviceAddress')
                  );
                  const showTimes = Boolean(
                    booking
                    && !showServices
                    && !booking.ready
                    && !booking.booked
                    && !(booking.technicianGroups && booking.technicianGroups.length > 0)
                    && ((booking.availableTimes && booking.availableTimes.length > 0) || (booking.chips || []).some(looksLikeTimeChip))
                  );
                  return (
                    <div
                      key={`${turn.role}-${index}`}
                      className={`max-w-[96%] px-3 py-2 text-sm leading-relaxed ${isUser ? 'self-end' : 'self-start border'}`}
                      style={isUser
                        ? { background: ACCENT, color: INK, borderRadius: 18 }
                        : { borderColor: `${TEXT}22`, color: TEXT, borderRadius: 18, minWidth: booking?.ready || booking?.booked || showServices ? '16.5rem' : undefined }}
                    >
                      {!isUser && <AaCoreAiName color={`${TEXT}99`} aiColor={ACCENT} className="mb-1.5" />}
                      {isUser ? <p className="m-0 whitespace-pre-wrap">{turn.content}</p> : <AskAiRichText text={turn.content} accentColor={ACCENT} />}
                      {!isUser && booking && (booking.ready || booking.booked) && (
                        <AskAiBookingReviewCard
                          booking={booking}
                          textColor={TEXT}
                          title={
                            booking.booked
                              ? (booking.recurring ? 'Recurring series booked' : 'Appointment booked')
                              : booking.recurring
                                ? 'Recurring series'
                                : 'Appointment summary'
                          }
                        />
                      )}
                      {showServices && booking?.serviceGroups && (
                        <ServiceChoices
                          groups={booking.serviceGroups}
                          preselectedIds={(booking.services || []).map((service) => service.id)}
                          disabled={stale}
                          onContinue={(services) => {
                            const names = services.map((service) => service.name);
                            const query = names.length === 1
                              ? `Book ${names[0]}`
                              : `Book ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
                            void ask(query, {
                              wanted: true,
                              serviceNames: names,
                              serviceIds: services.map((service) => service.id),
                              replaceServices: true,
                              candidateServiceNames: [],
                            });
                          }}
                        />
                      )}
                      {!isUser && booking?.technicianGroups && booking.technicianGroups.length > 0 && !booking.ready && !booking.booked && (
                        <div className="mt-2 space-y-2">
                          {booking.technicianGroups.map((group) => (
                            <div key={group.serviceId}>
                              <p className="m-0 mb-1 text-xs font-semibold">{group.serviceName}</p>
                              <div className="flex flex-wrap gap-1">
                                {group.professionals.map((person) => (
                                  <button
                                    key={person.id}
                                    type="button"
                                    disabled={stale}
                                    className="text-xs border rounded-full px-2 py-1 cursor-pointer disabled:opacity-60"
                                    style={{ borderColor: `${TEXT}22`, color: TEXT }}
                                    onClick={() => void ask(`${person.name} for ${group.serviceName}`, {
                                      wanted: true,
                                      sameProfessional: false,
                                      serviceProfessionals: [
                                        ...(booking.serviceProfessionals || []).filter((pick) => pick.serviceId !== group.serviceId),
                                        { serviceId: group.serviceId, professionalId: person.id, professionalName: person.name },
                                      ],
                                    })}
                                  >
                                    {person.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {showDates && (
                        <DateChoicePicker
                          options={booking?.dateOptions || []}
                          showCalendar={booking?.showCalendar}
                          accentColor={ACCENT}
                          accentInk={INK}
                          textColor={TEXT}
                          disabled={stale}
                          onPick={(ymd, label) => {
                            void ask(label || ymd, { wanted: true, date: ymd });
                          }}
                        />
                      )}
                      {showTimes && (
                        <TimeSlotGrid
                          times={booking?.availableTimes && booking.availableTimes.length > 0
                            ? booking.availableTimes
                            : (booking?.chips || []).filter(looksLikeTimeChip)}
                          textColor={TEXT}
                          disabled={stale}
                          onPick={(time, label) => {
                            const hhmm = /^([01]?\d|2[0-3]):[0-5]\d$/.test(time) ? time : undefined;
                            void ask(label, hhmm ? { wanted: true, time: hhmm } : { wanted: true });
                          }}
                        />
                      )}
                      {!isUser && (booking?.customerChoices || []).length > 0 && !booking?.ready && !booking?.booked && (
                        <div className="flex flex-col gap-1 mt-2">
                          {(booking?.customerChoices || []).map((choice) => (
                            <button
                              key={choice.id}
                              type="button"
                              disabled={stale}
                              className="text-left text-xs border px-2 py-1.5 cursor-pointer disabled:opacity-60"
                              style={{ borderColor: `${TEXT}22`, color: TEXT, borderRadius: 8 }}
                              onClick={() => void ask(choice.label, chipOverride(choice.label, booking))}
                            >
                              {choice.label}
                            </button>
                          ))}
                        </div>
                      )}
                      {!isUser && (booking?.chips || []).filter((chip) => !looksLikeTimeChip(chip) && !(booking?.customerChoices || []).some((choice) => choice.label === chip)).length > 0 && !booking?.ready && !booking?.booked && !showServices && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {(booking?.chips || []).filter((chip) => !looksLikeTimeChip(chip) && !(booking?.customerChoices || []).some((choice) => choice.label === chip)).map((chip) => (
                            <button
                              key={chip}
                              type="button"
                              disabled={stale}
                              className="text-xs bg-transparent border rounded-full px-2 py-1 cursor-pointer disabled:opacity-60"
                              style={{ borderColor: `${TEXT}22`, color: TEXT }}
                              onClick={() => void ask(chip, chipOverride(chip, booking))}
                            >
                              {chip}
                            </button>
                          ))}
                        </div>
                      )}
                      {!isUser && booking?.ready && !booking.booked && (
                        <button
                          type="button"
                          className="text-sm font-semibold px-3 py-2 w-full mt-3 cursor-pointer disabled:opacity-60"
                          style={{ background: ACCENT, color: INK, borderRadius: 10, border: 0 }}
                          disabled={stale}
                          onClick={() => void confirmBook(booking, index)}
                        >
                          {bookingBusy ? 'Booking…' : 'Confirm and Book'}
                        </button>
                      )}
                      {!isUser && booking?.booked && booking.appointmentUrl && (
                        <div className="mt-2">
                          <a
                            href={booking.appointmentUrl}
                            className="text-xs font-semibold no-underline px-2 py-1"
                            style={{ background: ACCENT, color: INK, borderRadius: 8 }}
                          >
                            View appointment
                          </a>
                        </div>
                      )}
                    </div>
                  );
                })}
                {pending && (
                  <div className="self-start px-3 py-2 text-sm italic border border-dashed" style={{ borderColor: `${TEXT}22`, color: `${TEXT}99`, borderRadius: 18 }}>
                    Thinking…
                  </div>
                )}
              </div>
              <form
                className="flex gap-2 p-3 border-t"
                style={{ borderColor: `${TEXT}14` }}
                onSubmit={(event) => {
                  event.preventDefault();
                  const query = input.trim();
                  if (!query) return;
                  setInput('');
                  void ask(query);
                }}
              >
                <input
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  placeholder="Book a customer or a walk-in"
                  aria-label="Message AACore AI"
                  className="flex-1 min-w-0 border px-3 py-2 text-sm"
                  style={{ borderColor: `${TEXT}22`, borderRadius: 10 }}
                />
                <button
                  type="submit"
                  disabled={pending || bookingBusy}
                  className="px-3 py-2 text-sm font-semibold cursor-pointer disabled:opacity-60"
                  style={{ background: ACCENT, color: INK, borderRadius: 10, border: 0 }}
                >
                  Send
                </button>
              </form>
              {status && (
                <p className="px-4 pb-3 m-0 text-xs" style={{ color: statusError ? '#b91c1c' : `${TEXT}99` }}>{status}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BusinessOwnerAskAiWidget;
